'use es6';

import { Map as ImmutableMap, List } from 'immutable';
import { getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { getPropertyFormatter, isFormattableProperty } from '../../../formatters/propertyFormatter';
import { FOLD_KEY_FIELD, FOLD_VALUE_FIELD } from '../../../transforms/fold';
import { DISPLAY_MEASURE, SUBTOTAL_DISPLAY_HINT } from '../crossTabVisualization';
import { getTotalMeasureAliasPivot, TOTAL_KEY_FIELD, SUBTOTAL_KEY_FIELD, GRAND_TOTAL_KEY_FIELD } from '../dataHelpers/totals';
const findMatchingDataPoint = (dataGroup, headerData, visualizationDisplayValue, fallbackDataPoint = ImmutableMap({})) => {
  const headerKeys = headerData.keySeq();
  const firstHeaderKey = headerKeys.first();
  const onlyDisplayColumn = visualizationDisplayValue === firstHeaderKey;
  const dataPoint = dataGroup.find(searchPoint => {
    const dataMatches = headerData.reduce((res, val, spKey) => {
      const newRes = searchPoint && searchPoint.get(spKey) === val;
      return res && newRes;
    }, true);
    return onlyDisplayColumn || dataMatches;
  }, null, fallbackDataPoint);
  return dataPoint;
};
const getOnClickFromEvents = (events, columnDisplayField) => {
  if (!events) return null;
  const getColumnOnClick = events.get('getColumnOnClick');
  if (getColumnOnClick) {
    const columnOnClick = getColumnOnClick(columnDisplayField);
    if (columnOnClick) {
      return columnOnClick;
    }
  }
  return events.get('onClick');
};
const getHandleClick = (visualization, dataPoint, columnDisplayField, isColumnsTotal, {
  isColumnSubtotal,
  columnSubtotalField
}, isRowTotal = false, {
  isRowSubtotal,
  rowSubtotalKey
} = {}) => {
  const {
    columns,
    events,
    rows
  } = visualization;
  const onClick = getOnClickFromEvents(events, columnDisplayField);
  if (!onClick) {
    return null;
  }
  const rowsList = rows.map(rowEncoding => rowEncoding.get('field'));
  const allColumnsList = columns.map(colEncoding => colEncoding.get('field'));
  // Columns include FOLD_KEY_FIELD field when folded, this shouldn't impact standard drilldowns
  // as they are not a real column on the reportDefinition, but removing it here for safety
  const columnsList = allColumnsList.contains(FOLD_KEY_FIELD) ? allColumnsList.pop() : allColumnsList;
  const subtotalColList = columnsList.take(columnsList.findIndex(colItem => colItem === rowSubtotalKey) + 1);
  const subtotalRowsList = rowsList.take(rowsList.findIndex(rowItem => rowItem === columnSubtotalField) + 1);
  const generateOnClick = filterList => () => onClick({
    row: dataPoint.filter((_, dataPointKey) => filterList.includes(dataPointKey)),
    field: columnDisplayField,
    levelOfDetail: filterList.toJS()
  });

  /*
    The logic for a generating the filter for a column looks like (flip this for rows)
    Column total: This is a value shared by all rows for a given column. Do not filter by any row field.
    Column Subtotal: This is a value shared by a group of rows, filter includes the row field(s) being subtotaled but NOT the row field(s) whose values would roll up into this subtotal
    standard value: This is a value specific to a single row, the filter will include all row fields
  */
  let rowsFilter = columnsList;
  if (isRowTotal) {
    rowsFilter = List();
  }
  if (isRowSubtotal) {
    rowsFilter = subtotalColList;
  }
  let columnsFilter = rowsList;
  if (isColumnsTotal) {
    columnsFilter = List();
  }
  if (isColumnSubtotal) {
    columnsFilter = subtotalRowsList;
  }
  return generateOnClick(rowsFilter.concat(columnsFilter));
};
const getRowTotalElement = (dataGroup, dataColumn, visualization, {
  rowTotalKey,
  rowTotalKeyV2
}, isColumnsTotal, {
  isColumnSubtotal,
  columnSubtotalField
}) => {
  const {
    data,
    dataset,
    // These total values are for backwards compatability for existing cross tab reports
    // Newer cross tab reports will use the totalMeasureAlias style totals which support subtotals
    grandTotal,
    columnTotal
  } = visualization;
  const fullDataset = data.get(dataset);
  const headerData = dataColumn.get('headerData');
  const columnAlign = dataColumn.get('align');
  const displayField = headerData.get(DISPLAY_MEASURE);
  const totalRowField = headerData.get(rowTotalKeyV2);
  const dataPoint = dataGroup.get(0);
  let columnDisplayField = displayField;
  let rowTotalValue;
  let totalField;
  if (isColumnsTotal) {
    const grandTotalMeasureAlias = headerData.get(GRAND_TOTAL_KEY_FIELD);
    const grandTotalMeasureDataPoint = dataPoint.getIn([grandTotalMeasureAlias]);
    rowTotalValue = grandTotalMeasureDataPoint || dataPoint.getIn([grandTotal.first()]) || 0;
    totalField = grandTotalMeasureDataPoint ? grandTotalMeasureAlias : columnTotal.first();
    columnDisplayField = grandTotalMeasureAlias;
  } else if (isColumnSubtotal) {
    totalField = getTotalMeasureAliasPivot(displayField, columnSubtotalField);
    rowTotalValue = dataPoint.getIn([totalField]) || 0;
  } else {
    const totalMeasureAlias = getTotalMeasureAliasPivot(displayField, totalRowField);
    const totalMeasureDataPoint = dataPoint.getIn([totalMeasureAlias]);
    totalField = totalMeasureDataPoint ? totalMeasureAlias : rowTotalKey;
    rowTotalValue = totalMeasureDataPoint || dataPoint.getIn([rowTotalKey]) || 0;
  }
  const handleClick = getHandleClick(visualization, dataPoint, columnDisplayField, isColumnsTotal, {
    isColumnSubtotal,
    columnSubtotalField
  }, true);
  const displayProperty = fullDataset.getIn(['properties', displayField]);
  const maybeFormattableCellDisplayProperty = fullDataset.getIn(['properties', totalField]);
  const cellDisplayProperty = isFormattableProperty(displayProperty) && isFormattableProperty(maybeFormattableCellDisplayProperty) && displayProperty.get('formattingObject') ? maybeFormattableCellDisplayProperty.set('formattingObject', displayProperty.get('formattingObject')).set('formattingType', displayProperty.get('formattingType')) : maybeFormattableCellDisplayProperty;
  const totalsPropFormatter = getPropertyFormatter(cellDisplayProperty);
  return ImmutableMap({
    label: getReferenceLabel(fullDataset, totalField, rowTotalValue, totalsPropFormatter(rowTotalValue)),
    onClick: handleClick,
    align: columnAlign
  });
};
const getRowSubtotalElement = (dataGroup, dataColumn, visualization, isColumnsTotal, {
  isColumnSubtotal,
  columnSubtotalField
}) => {
  const {
    data,
    dataset,
    values,
    rows
  } = visualization;
  if (!rows.length) {
    return ImmutableMap();
  }
  const fullDataset = data.get(dataset);
  const visualizationDisplayValue = values.getIn([0, 'field']);
  const isFolded = visualizationDisplayValue === FOLD_VALUE_FIELD;
  const headerData = dataColumn.get('headerData');
  const columnAlign = dataColumn.get('align');
  const rowSubtotalKey = headerData.get(SUBTOTAL_KEY_FIELD);
  const dataPoint = findMatchingDataPoint(dataGroup, headerData.delete(SUBTOTAL_KEY_FIELD).delete(SUBTOTAL_DISPLAY_HINT), visualizationDisplayValue);
  const foldKey = dataPoint.get(FOLD_KEY_FIELD);
  const displayValue = isFolded ? foldKey : visualizationDisplayValue;
  let columnVariance = rows.last().get('field');
  if (isColumnsTotal) {
    columnVariance = undefined;
  }
  if (isColumnSubtotal) {
    columnVariance = columnSubtotalField;
  }
  const subtotalMeasureAlias = getTotalMeasureAliasPivot(displayValue, columnVariance, rowSubtotalKey);
  const dataPointDisplayValue = dataPoint.get(subtotalMeasureAlias) || 0;
  const displayProperty = fullDataset.getIn(['properties', displayValue]);
  const propFormatter = getPropertyFormatter(displayProperty);
  const handleClick = getHandleClick(visualization, dataPoint, displayValue, isColumnsTotal, {
    isColumnSubtotal,
    columnSubtotalField
  }, false, {
    isRowSubtotal: true,
    rowSubtotalKey
  });
  return ImmutableMap({
    label: getReferenceLabel(fullDataset, visualizationDisplayValue, dataPointDisplayValue, propFormatter(dataPointDisplayValue)),
    align: columnAlign,
    onClick: handleClick
  });
};
const getRowDataElement = (dataGroup, dataColumn, visualization, isColumnsTotal = false, {
  isColumnSubtotal,
  columnSubtotalField
}, originalVisualization) => {
  const {
    columns,
    data,
    dataset,
    rows,
    values,
    // These total values are for backwards compatability for existing cross tab reports
    // Newer cross tab reports will use the totalMeasureAlias style totals which support subtotals
    columnTotal
  } = visualization;
  const originalValues = originalVisualization && originalVisualization.values;
  const visualizationDisplayValue = values.getIn([0, 'field']);
  const isFolded = visualizationDisplayValue === FOLD_VALUE_FIELD;
  const fullDataset = data.get(dataset);
  const columnAlign = dataColumn.get('align');
  const headerData = dataColumn.get('headerData');

  // Will be used to provide row/col data for onClick
  const fallbackDataPoint = rows.toMap().mapEntries(([__, rowEncoding]) => {
    const field = rowEncoding.get('field');
    const rowValue = dataGroup.getIn([0, field]);
    return [field, rowValue];
  }).merge(headerData);
  const dataPoint = findMatchingDataPoint(dataGroup, headerData, visualizationDisplayValue, fallbackDataPoint);
  const foldKey = dataPoint.get(FOLD_KEY_FIELD);
  const lastColumn = columns.last() ? columns.last().get('field') : undefined;
  const lastColumnFolded = columns.getIn([columns.count() - 2, 'field']);
  let cellDisplayValue = visualizationDisplayValue;
  let foldedDisplayValue = foldKey;
  if (isColumnsTotal) {
    const columnTotalAlias = getTotalMeasureAliasPivot(visualizationDisplayValue, undefined, lastColumn);
    cellDisplayValue = dataPoint.get(columnTotalAlias) ? columnTotalAlias : columnTotal.first();
    if (isFolded) {
      const foldedTotalAlias = getTotalMeasureAliasPivot(foldKey, undefined, lastColumnFolded === FOLD_KEY_FIELD ? undefined : lastColumnFolded);
      foldedDisplayValue = dataPoint.get(foldedTotalAlias) ? foldedTotalAlias : columnTotal.find(valueInList => valueInList.endsWith(foldKey));
    }
  }
  if (isColumnSubtotal) {
    cellDisplayValue = getTotalMeasureAliasPivot(visualizationDisplayValue, columnSubtotalField, lastColumn);
    if (isFolded) {
      foldedDisplayValue = getTotalMeasureAliasPivot(foldKey, columnSubtotalField, lastColumnFolded === FOLD_KEY_FIELD ? undefined : lastColumnFolded);
    }
  }
  const displayValue = isFolded ? foldedDisplayValue : cellDisplayValue;
  const dataPointDisplayValue = dataPoint.get(displayValue) || 0;
  const columnDisplayField = isFolded ? foldKey : visualizationDisplayValue;
  const handleClick = getHandleClick(visualization, dataPoint, columnDisplayField, isColumnsTotal, {
    isColumnSubtotal,
    columnSubtotalField
  });
  const maybeFormattableDisplayProperty = fullDataset.getIn(['properties', isFolded ? foldedDisplayValue : displayValue]);
  const valueProperty = originalValues && originalValues.find(val => isFolded ? foldedDisplayValue.includes(val.field) : displayValue.includes(val.field));
  const displayProperty = valueProperty && isFormattableProperty(maybeFormattableDisplayProperty) && valueProperty.get('formattingObject') ? maybeFormattableDisplayProperty.set('formattingObject', valueProperty.get('formattingObject')).set('formattingType', valueProperty.get('formattingType')) : maybeFormattableDisplayProperty;
  const propFormatter = getPropertyFormatter(displayProperty);
  return ImmutableMap({
    label: getReferenceLabel(fullDataset, displayValue, dataPointDisplayValue, propFormatter(dataPointDisplayValue)),
    onClick: handleClick,
    align: columnAlign
  });
};
export const getRowDataElements = (visualization, dataGroup, columnEncodingsWithLabels, isColumnsTotal = false, {
  isColumnSubtotal,
  columnSubtotalField
} = {}, originalVisualization) => {
  const {
    values,
    // These total values are for backwards compatability for existing cross tab reports
    // Newer cross tab reports will use the totalMeasureAlias style totals which support subtotals
    rowTotal
  } = visualization;
  const dataColumns = columnEncodingsWithLabels.getIn([columnEncodingsWithLabels.count() - 1, 'encodingLabels']);
  const visualizationDisplayValue = values.getIn([0, 'field']);
  const rowElements = dataColumns.map(dataColumn => {
    const headerData = dataColumn.get('headerData');

    // Handle the case of no display value set - we should only see this state during construction.
    // We will show the header and row titles, with all data cells empty.
    // This is intended to help a user understand the structure of the cross tabulation they are building
    if (!isColumnsTotal && !isColumnSubtotal && !visualizationDisplayValue) {
      return ImmutableMap({});
    }
    const rowTotalKeyV2 = headerData.findKey((_, key) => key === TOTAL_KEY_FIELD);
    const rowTotalKey = headerData.find(val => val === rowTotal.first());
    if (rowTotalKeyV2 || rowTotalKey) {
      return getRowTotalElement(dataGroup, dataColumn, visualization, {
        rowTotalKey,
        rowTotalKeyV2
      }, isColumnsTotal, {
        isColumnSubtotal,
        columnSubtotalField
      }, originalVisualization);
    }
    const rowSubtotalKey = headerData.findKey((_, key) => key === SUBTOTAL_KEY_FIELD);
    if (rowSubtotalKey) {
      return getRowSubtotalElement(dataGroup, dataColumn, visualization, isColumnsTotal, {
        isColumnSubtotal,
        columnSubtotalField
      }, originalVisualization);
    }
    return getRowDataElement(dataGroup, dataColumn, visualization, isColumnsTotal, {
      isColumnSubtotal,
      columnSubtotalField
    }, originalVisualization);
  });
  return rowElements;
};
export const __TESTABLE__ = {
  getHandleClick
};